// src/pages/Route-page/Routepage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaRoute,
  FaTruck,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaArrowRight,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import { BASE_URL } from '../../api';

const API_BASE = `${BASE_URL}/api/routemasters`;

const Routepage = () => {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewRoute, setViewRoute] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token') || '';

  const fetchRoutes = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_BASE, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch routes');
      const result = await response.json();
      if (result.data && Array.isArray(result.data)) {
        setRoutes(result.data);
      }
    } catch (err) {
      toast.error('Error fetching routes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleEdit = (route) => navigate('/add-route', { state: { route } });

  const handleDelete = async (id, origin, dest) => {
    const result = await Swal.fire({
      title: 'Delete Route?',
      text: `Are you sure you want to delete route ${origin || ''} → ${dest || ''}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setRoutes((prev) => prev.filter((r) => r._id !== id));
          toast.success('Route deleted successfully');
        } else {
          toast.error('Delete failed');
        }
      } catch (err) {
        toast.error('Delete failed');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (routes.length === 0) {
      toast.info('No route data to export');
      return;
    }
    const exportRows = filteredRoutes.map((r, idx) => ({
      'S.No': idx + 1,
      Origin: r.originid?.areaname || '-',
      Destination: r.destinationid?.areaname || '-',
      Date: r.routedate ? r.routedate.split('T')[0] : '-',
      Timing: r.routetiming || '-',
      Vehicle: r.vehiclenumberText || r.vehiclenumber || '-',
      Driver: r.drivername || '-',
      Hub: r.hub || '-',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Routes');
    XLSX.writeFile(workbook, `Route_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Route list exported to Excel successfully!');
  };

  const filteredRoutes = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return routes;
    return routes.filter((r) =>
      (r.originid?.areaname || '').toLowerCase().includes(term) ||
      (r.destinationid?.areaname || '').toLowerCase().includes(term) ||
      (r.vehiclenumberText || r.vehiclenumber || '').toLowerCase().includes(term) ||
      (r.drivername || '').toLowerCase().includes(term) ||
      (r.hub || '').toLowerCase().includes(term)
    );
  }, [routes, searchTerm]);

  const totalItems = filteredRoutes.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedRoutes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRoutes.slice(start, start + pageSize);
  }, [filteredRoutes, currentPage, pageSize]);

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            Route Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage origin-destination transit corridors, vehicle dispatch timings & driver allocations
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => navigate('/add-route')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New Route</span>
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Routes</p>
            <p className="text-2xl font-bold text-gray-800">{routes.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaRoute />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Vehicles Mapped</p>
            <p className="text-2xl font-bold text-green-600">
              {[...new Set(routes.map((r) => r.vehiclenumberText || r.vehiclenumber).filter(Boolean))].length}
            </p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaTruck />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Drivers Active</p>
            <p className="text-2xl font-bold text-gray-700">
              {[...new Set(routes.map((r) => r.drivername).filter(Boolean))].length}
            </p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaUser />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active Hubs</p>
            <p className="text-2xl font-bold text-indigo-600">
              {[...new Set(routes.map((r) => r.hub).filter(Boolean))].length}
            </p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaMapMarkerAlt />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search origin, destination, vehicle, driver..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading routes...</p>
          </div>
        ) : paginatedRoutes.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaRoute className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No routes found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedRoutes.map((r, idx) => {
              const sNo = (currentPage - 1) * pageSize + idx + 1;
              const origin = r.originid?.areaname || 'Origin';
              const dest = r.destinationid?.areaname || 'Destination';

              return (
                <div
                  key={r._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm">
                          <FaRoute />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-base leading-tight flex items-center gap-1.5">
                              <span>{origin}</span>
                              <FaArrowRight className="text-blue-500 text-xs" />
                              <span>{dest}</span>
                            </h3>
                          </div>
                          <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                        </div>
                      </div>

                      {r.hub && (
                        <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                          {r.hub}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Vehicle</span>
                        <span className="font-mono font-bold text-blue-600 truncate block">
                          {r.vehiclenumberText || r.vehiclenumber || 'Unassigned'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Driver</span>
                        <span className="font-semibold text-gray-800 truncate block">
                          {r.drivername || '-'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="text-amber-500" />
                        <span>{r.routedate ? r.routedate.split('T')[0] : '-'}</span>
                      </div>
                      {r.routetiming && (
                        <span className="text-gray-400">⏱ {r.routetiming}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewRoute(r)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleEdit(r)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(r._id, origin, dest)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Origin</th>
                  <th className="py-3 px-4">Destination</th>
                  <th className="py-3 px-4">Vehicle</th>
                  <th className="py-3 px-4">Driver</th>
                  <th className="py-3 px-4">Schedule</th>
                  <th className="py-3 px-4">Hub</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedRoutes.map((r, idx) => {
                  const sNo = (currentPage - 1) * pageSize + idx + 1;
                  const origin = r.originid?.areaname || '-';
                  const dest = r.destinationid?.areaname || '-';

                  return (
                    <tr key={r._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3.5 px-4 font-bold text-gray-900">{origin}</td>
                      <td className="py-3.5 px-4 font-bold text-gray-900">{dest}</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-blue-600">{r.vehiclenumberText || r.vehiclenumber || '-'}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{r.drivername || '-'}</td>
                      <td className="py-3.5 px-4 text-xs">
                        <div>{r.routedate ? r.routedate.split('T')[0] : '-'}</div>
                        <div className="text-gray-400">{r.routetiming || ''}</div>
                      </td>
                      <td className="py-3.5 px-4 font-semibold text-indigo-600">{r.hub || '-'}</td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewRoute(r)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleEdit(r)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(r._id, origin, dest)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: View Route Details */}
      {viewRoute && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {viewRoute.originid?.areaname || 'Origin'} → {viewRoute.destinationid?.areaname || 'Destination'}
              </h3>
              <button onClick={() => setViewRoute(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Vehicle</span>
                  <strong className="font-mono text-blue-600 text-base">
                    {viewRoute.vehiclenumberText || viewRoute.vehiclenumber || '-'}
                  </strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Driver</span>
                  <strong className="text-gray-800 text-base">{viewRoute.drivername || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Route Date</span>
                  <strong className="text-gray-800">
                    {viewRoute.routedate ? viewRoute.routedate.split('T')[0] : '-'}
                  </strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Timing</span>
                  <strong className="text-gray-800">{viewRoute.routetiming || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Hub</span>
                  <strong className="text-indigo-600">{viewRoute.hub || '-'}</strong>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewRoute(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewRoute;
                  setViewRoute(null);
                  handleEdit(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit Route
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Routepage;